import React, { useMemo, useState } from "react";
import { useTimezone } from "../../hooks/useTimezone";
import {
  PageHeader,
  StatsCards,
  TabSelector,
  DataTable,
  DataTableCard,
  Pagination2,
  ColumnSelector,
} from "../common";
import { RegularizationTableSkeleton } from "./Skeleton";
import {
  isApprovedWorkflowStatus,
  isRejectedWorkflowStatus,
  WorkflowStatusCode,
} from "../../utils/constants";
import {
  getHistoryColumns,
  getPendingColumns,
  getRegularizationTypeLabel,
  RejectModal,
  RequestDetailsModal,
  useRegularizationManagement,
  ViewModeTabs,
  STAT_CARDS,
  DEFAULT_VISIBLE_KEYS,
} from "./regularization-management";

type StatusTab = "pending" | "history";

interface RegularizationManagementProps {
  isManagerView?: boolean;
  title?: string;
  description?: string;
}

const RegularizationManagement: React.FC<RegularizationManagementProps> = ({
  title = "Regularization Management",
  description,
}) => {
  const { formatDate, formatTime } = useTimezone();
  const [activeTab, setActiveTab] = useState<StatusTab>("pending");
  const [visibleKeys, setVisibleKeys] = useState<string[]>(DEFAULT_VISIBLE_KEYS);

  const toggleVisibility = (key: string) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const {
    isAdmin,

    pendingPage,
    setPendingPage,
    pendingLimit,
    setPendingLimit,
    pendingRequests,
    pendingLoading,
    pendingTotal,
    pendingViewMode,
    setPendingViewMode,

    historyRequests,
    historyLoading,
    historyPage,
    setHistoryPage,
    historyLimit,
    setHistoryLimit,
    historyTotal,
    historyViewMode,
    setHistoryViewMode,

    selectedRequest,
    showDetailsModal,
    setShowDetailsModal,
    showRejectModal,
    setShowRejectModal,
    rejectReason,
    setRejectReason,

    processing,

    handleViewDetails,
    handleApproveClick,
    handleRejectClick,
    handleModalReject,
    canActOnRequest,
    getStatusColor,
  } = useRegularizationManagement();

  const currentPage = activeTab === "pending" ? pendingPage : historyPage;
  const setCurrentPage = activeTab === "pending" ? setPendingPage : setHistoryPage;
  const currentLimit = activeTab === "pending" ? pendingLimit : historyLimit;
  const setCurrentLimit = activeTab === "pending" ? setPendingLimit : setHistoryLimit;
  const currentRequests = activeTab === "pending" ? pendingRequests : historyRequests;
  const currentLoading = activeTab === "pending" ? pendingLoading : historyLoading;
  const currentTotal = activeTab === "pending" ? pendingTotal : historyTotal;

  const stats = useMemo(
    () => ({
      total: pendingTotal + historyTotal,
      pending: pendingTotal,
      approved: historyRequests.filter((r) =>
        isApprovedWorkflowStatus(r.status as WorkflowStatusCode),
      ).length,
      rejected: historyRequests.filter((r) =>
        isRejectedWorkflowStatus(r.status as WorkflowStatusCode),
      ).length,
    }),
    [pendingTotal, historyTotal, historyRequests],
  );

  const allColumns = useMemo(() => {
    if (activeTab === "pending") {
      return getPendingColumns({
        formatDate,
        formatTime,
        getRegularizationTypeLabel,
        getStatusColor,
        handleViewDetails,
        handleApproveClick,
        handleRejectClick,
        canActOnRequest,
        processing,
      });
    }
    return getHistoryColumns({
      formatDate,
      formatTime,
      getRegularizationTypeLabel,
      getStatusColor,
      handleViewDetails,
    });
  }, [
    activeTab,
    formatDate,
    formatTime,
    getStatusColor,
    handleViewDetails,
    handleApproveClick,
    handleRejectClick,
    canActOnRequest,
    processing,
  ]);

  const configuredColumns = useMemo(() => {
    return allColumns.filter(
      (col) => col.required || visibleKeys.includes(col.key),
    );
  }, [allColumns, visibleKeys]);

  return (
    <div className="w-full">
      <PageHeader
        title={title}
        breadcrumbs={[
          { label: "Home", path: "/dashboard" },
          { label: "Regularization Mgmt" },
        ]}
        description={description ?? `${pendingTotal} requests awaiting review`}
        actions={
          <div className="flex flex-col items-end gap-2.5">
            {isAdmin && (
              <ViewModeTabs
                viewMode={
                  activeTab === "pending" ? pendingViewMode : historyViewMode
                }
                setViewMode={
                  activeTab === "pending" ? setPendingViewMode : setHistoryViewMode
                }
                setPage={setCurrentPage}
              />
            )}
            <TabSelector<"pending" | "history">
              tabs={[
                { k: "pending", label: "Pending" },
                { k: "history", label: "History" },
              ]}
              active={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setCurrentPage(1);
              }}
            />
          </div>
        }
      />

      <div className="mb-5 sm:mb-6">
        <StatsCards
          stats={stats}
          cards={STAT_CARDS}
          variant="simple-icon"
          valueOnTop
        />
      </div>

      <DataTableCard
        title="Regularization Requests"
        count={currentTotal}
        toolbar={
          <ColumnSelector
            columns={allColumns}
            visibleKeys={visibleKeys}
            onToggle={toggleVisibility}
          />
        }
      >
        <DataTable
          columns={configuredColumns}
          rows={currentRequests}
          rowKey={(r) => r.id}
          loading={currentLoading}
          skeleton={<RegularizationTableSkeleton />}
          emptyMessage={
            activeTab === "pending"
              ? "No pending requests found"
              : "No historical requests found"
          }
          responsive="auto"
        />
        {currentTotal > currentLimit && (
          <Pagination2
            total={currentTotal}
            page={currentPage}
            pageSize={currentLimit}
            onPageChange={setCurrentPage}
            onPageSizeChange={(l) => {
              setCurrentLimit(l);
              setCurrentPage(1);
            }}
          />
        )}
      </DataTableCard>

      {selectedRequest && (
        <>
          <RequestDetailsModal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            request={selectedRequest}
            formatDate={formatDate}
            formatTime={formatTime}
            getRegularizationTypeLabel={getRegularizationTypeLabel}
            getStatusColor={getStatusColor}
          />
          <RejectModal
            isOpen={showRejectModal}
            onClose={() => setShowRejectModal(false)}
            request={selectedRequest}
            rejectReason={rejectReason}
            setRejectReason={setRejectReason}
            onReject={handleModalReject}
            processing={processing}
            formatDate={formatDate}
          />
        </>
      )}
    </div>
  );
};

export default RegularizationManagement;
